import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { api, jsonBody, setSessionToken, getSessionToken } from "./api";
import type { Provider, SessionResult, Subject, ListResponse } from "./types";
import { Button, Field, Notice, Spinner } from "./components";

interface AuthContextValue {
  subject: Subject | null;
  loading: boolean;
  refresh: () => Promise<void>;
  completeSession: (session: SessionResult) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [subject, setSubject] = useState<Subject | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!getSessionToken()) {
      setSubject(null);
      setLoading(false);
      return;
    }
    try {
      setSubject(await api<Subject>("/api/v1/me"));
    } catch {
      setSubject(null);
    } finally {
      setLoading(false);
    }
  };
  const refreshEvent = useEffectEvent(refresh);

  useEffect(() => {
    void refreshEvent();
    const handleSession = () => void refreshEvent();
    window.addEventListener("keymarket:session", handleSession);
    return () => window.removeEventListener("keymarket:session", handleSession);
  }, []);

  const completeSession = async (session: SessionResult) => {
    setSessionToken(session.session_token);
    await refresh();
  };

  const logout = async () => {
    try {
      await api<void>("/api/v1/auth/logout", { method: "POST" });
    } finally {
      setSessionToken("");
      setSubject(null);
    }
  };

  return <AuthContext value={{ subject, loading, refresh, completeSession, logout }}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}

type AuthMode = "login" | "register" | "reset";
type AuthAction = "verify-email" | "reset-password" | "accept-invitation";

export function AuthPage() {
  const action = authActionForPath(window.location.pathname);
  return action ? <AuthActionPage action={action} token={new URLSearchParams(window.location.search).get("token") ?? ""} /> : <AccountAccessPage />;
}

interface ActivationProof {
  id: string;
  provider_name: string;
  provider_code: string;
  masked_secret: string;
  review_status: string;
  automated_test_status: string;
  job_status: string;
  attempt_count: number;
  next_attempt_at?: string;
}

interface ActivationSecurity {
  email?: string;
  email_verified_at?: string | null;
}

export function OrganizationActivationPage() {
  const { subject, refresh, logout } = useAuth();
  const [security, setSecurity] = useState<ActivationSecurity>({});
  const [proofs, setProofs] = useState<ActivationProof[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<"refresh" | "resend" | "submit-proof" | "logout" | "">("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showProofForm, setShowProofForm] = useState(false);
  const [proofProvider, setProofProvider] = useState("");
  const [capabilityKey, setCapabilityKey] = useState("");
  const [proofReason, setProofReason] = useState("");
  const isSupplier = subject?.organization_type === "supplier";

  const loadActivation = async () => {
    if (!subject) return;
    setLoading(true);
    setError("");
    try {
      const [securityResult, proofResult, providerResult] = await Promise.all([
        api<ActivationSecurity>("/api/v1/me/security"),
        isSupplier
          ? api<ListResponse<ActivationProof>>("/api/v1/supplier/capability-proofs")
          : Promise.resolve({ items: [] } as ListResponse<ActivationProof>),
        isSupplier
          ? api<ListResponse<Provider>>("/api/v1/catalog/providers")
          : Promise.resolve({ items: [] } as ListResponse<Provider>),
      ]);
      setSecurity(securityResult);
      setProofs(proofResult.items);
      setProviders(providerResult.items);
      setProofProvider((current) => current || proofResult.items[0]?.provider_code || providerResult.items[0]?.code || "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法读取组织启用状态");
    } finally {
      setLoading(false);
    }
  };
  const loadActivationEvent = useEffectEvent(loadActivation);

  useEffect(() => {
    void loadActivationEvent();
  }, [subject?.organization_id, isSupplier]);

  if (!subject) return null;
  const proof = proofs[0];
  const emailVerified = Boolean(security.email_verified_at);
  const automatedStatus = proof?.automated_test_status ?? "queued";
  const reviewStatus = proof?.review_status ?? "pending";
  const automatedDone = automatedStatus === "passed";
  const reviewDone = reviewStatus === "passed";
  const automatedFailed = ["failed", "unsupported", "expired"].includes(automatedStatus);
  const reviewFailed = ["failed", "expired"].includes(reviewStatus);
  const canReplaceProof = isSupplier && !loading && (!proof || automatedFailed || reviewFailed);
  const statusLabel = (status: string) => ({
		queued: "排队中", pending: "待审核", running: "检测中", passed: "已通过",
    failed: "未通过", retrying: "等待重试", completed: "已完成", cancelled: "已取消",
    unsupported: "暂不支持自动检测", expired: "已失效",
  }[status] ?? status);

  const refreshStatus = async () => {
    setBusyAction("refresh");
    setMessage("");
    setError("");
    try {
      await Promise.all([loadActivation(), refresh()]);
      setMessage("启用状态已刷新");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "刷新失败");
    } finally {
      setBusyAction("");
    }
  };

  const resendVerification = async () => {
    setBusyAction("resend");
    setMessage("");
    setError("");
    try {
      await api("/api/v1/me/email-verification/resend", { method: "POST" });
      setMessage("验证邮件已重新进入发送队列");
      await loadActivation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "验证邮件发送失败");
    } finally {
      setBusyAction("");
    }
  };

  const submitCapabilityProof = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusyAction("submit-proof");
    setMessage("");
    setError("");
    try {
      await api<ActivationProof>("/api/v1/supplier/capability-proofs", {
        method: "POST",
        ...jsonBody({ provider_code: proofProvider, capability_key: capabilityKey, reason: proofReason }),
      });
      setCapabilityKey("");
      setProofReason("");
      setShowProofForm(false);
      setMessage("新的能力证明已加密提交，正在进入自动检测队列");
      await loadActivation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "能力证明提交失败");
    } finally {
      setBusyAction("");
    }
  };

  return <AuthLayout>
    <section className="auth-card activation-card" aria-labelledby="activation-title">
      <header>
        <p className="activation-kicker">ORGANIZATION ACTIVATION</p>
		<h2 id="activation-title">{isSupplier ? "供应商能力审核中" : "平台组织审核中"}</h2>
		<p className="auth-card-intro">{isSupplier
			? "审核通过前不会开放商品、KEY、报价、销售、对账和提现，避免未验证主体进入交易链路。"
			: "完成企业邮箱验证并通过运营审核后，采购市场、资金、接收平台和 NewAPI 工作台才会开放。"}</p>
      </header>

      <div className="activation-account">
        <div><span>登录账号</span><strong>{security.email ?? subject.email}</strong></div>
        <div><span>组织编号</span><code>{subject.organization_id}</code></div>
      </div>

      <ol className="activation-steps" aria-label="组织启用进度">
        <li className="done"><span>1</span><div><strong>组织账号已建立</strong><small>身份、组织和审计记录已经创建</small></div><b>完成</b></li>
        <li className={emailVerified ? "done" : "pending"}><span>2</span><div><strong>验证企业邮箱</strong><small>{emailVerified ? "邮箱所有权已经确认" : "请使用邮件中的一次性链接完成验证"}</small></div><b>{emailVerified ? "完成" : "待验证"}</b></li>
        {isSupplier && <li className={automatedDone ? "done" : automatedFailed ? "failed" : "pending"}><span>3</span><div><strong>能力证明自动检测</strong><small>{proof ? `${proof.provider_name} / ${proof.masked_secret} / 已尝试 ${proof.attempt_count} 次` : "正在建立安全检测任务"}</small></div><b>{statusLabel(automatedStatus)}</b></li>}
		{isSupplier && <li className={reviewDone ? "done" : reviewFailed ? "failed" : "pending"}><span>4</span><div><strong>运营人工审核</strong><small>审核结论和理由将写入不可变操作记录</small></div><b>{statusLabel(reviewStatus)}</b></li>}
		{!isSupplier && <li className={subject.organization_status === "active" ? "done" : subject.organization_status === "closed" ? "failed" : "pending"}><span>3</span><div><strong>运营人工审核</strong><small>平台组织资料、邮箱状态、结论和理由将写入不可变审计记录</small></div><b>{subject.organization_status === "active" ? "已通过" : subject.organization_status === "closed" ? "未通过" : "待审核"}</b></li>}
      </ol>

      {proof && <div className="activation-proof-summary">
        <span>证明平台<strong>{proof.provider_name}</strong></span>
        <span>检测任务<strong>{statusLabel(proof.job_status)}</strong></span>
        <span>人工审核<strong>{statusLabel(proof.review_status)}</strong></span>
      </div>}
      {loading && <div className="activation-loading"><Spinner small /><span>正在核对最新状态</span></div>}
      {error && <Notice tone="danger">{error}</Notice>}
      {message && <Notice tone="success">{message}</Notice>}
      <p className="activation-help">审核期间 KEY 只以密文保存，页面、日志和审核队列均不会返回完整内容。检测失败或不支持自动检测时，可以在当前页面提交新的能力证明。</p>

      {canReplaceProof && !showProofForm && <Button tone="quiet" data-audit-route="/activation" disabled={Boolean(busyAction)} onClick={() => setShowProofForm(true)}>提交新的能力证明</Button>}
      {canReplaceProof && showProofForm && <form className="activation-resubmit" aria-label="重新提交能力证明" onSubmit={(event) => void submitCapabilityProof(event)}>
        <div className="activation-resubmit-heading">
          <strong>更换能力证明</strong>
          <span>提交后，当前待审证明会失效并重新进入自动检测与人工审核。</span>
        </div>
        <label className="field">
          <span>能力证明平台</span>
          <select value={proofProvider} onChange={(event) => setProofProvider(event.target.value)} required>
            {providers.map((provider) => <option key={provider.id} value={provider.code}>{provider.display_name}</option>)}
          </select>
        </label>
        <Field label="新的能力证明 KEY" type="password" autoComplete="off" value={capabilityKey} onChange={(event) => setCapabilityKey(event.target.value)} required maxLength={64 * 1024} hint="仅用于固定上游地址的能力检测，提交后不再返回完整内容" />
        <Field label="重提理由" value={proofReason} onChange={(event) => setProofReason(event.target.value)} required minLength={3} maxLength={1000} placeholder="说明更换原因，不要填写 KEY 或其他凭据" />
        <div className="activation-resubmit-actions">
          <Button type="button" tone="quiet" data-audit-route="/activation" disabled={Boolean(busyAction)} onClick={() => {
            setCapabilityKey("");
            setProofReason("");
            setShowProofForm(false);
          }}>取消</Button>
          <Button type="submit" data-audit-route="/activation" disabled={Boolean(busyAction) || !proofProvider || !capabilityKey.trim() || proofReason.trim().length < 3}>{busyAction === "submit-proof" ? "加密提交中" : "加密提交并重新检测"}</Button>
        </div>
      </form>}

      <div className="activation-actions">
        {!emailVerified && <Button tone="quiet" data-audit-route="/activation" disabled={Boolean(busyAction)} onClick={() => void resendVerification()}>{busyAction === "resend" ? "发送中" : "重新发送验证邮件"}</Button>}
        <Button data-audit-route="/activation" disabled={Boolean(busyAction)} onClick={() => void refreshStatus()}>{busyAction === "refresh" ? "刷新中" : "刷新审核状态"}</Button>
        <Button tone="quiet" data-audit-route="/activation" disabled={Boolean(busyAction)} onClick={() => {
          setBusyAction("logout");
          void logout().finally(() => {
            window.history.replaceState({}, "", "/login");
            window.dispatchEvent(new PopStateEvent("popstate"));
          });
        }}>安全退出</Button>
      </div>
    </section>
  </AuthLayout>;
}

function AccountAccessPage() {
  const { completeSession } = useAuth();
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/login";
  const mode: AuthMode = pathname === "/register" ? "register" : pathname === "/forgot-password" ? "reset" : "login";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [challenge, setChallenge] = useState("");
  const [providers, setProviders] = useState<Provider[]>([]);

  useEffect(() => {
    if (mode !== "register") return;
    api<ListResponse<Provider>>("/api/v1/catalog/providers")
      .then((result) => setProviders(result.items))
      .catch(() => setProviders([]));
  }, [mode]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (challenge) {
        const session = await api<SessionResult>("/api/v1/auth/mfa/verify", {
          method: "POST",
          ...jsonBody({ challenge_token: challenge, code: form.get("mfa_code") }),
        });
        await completeSession(session);
        return;
      }
      if (mode === "login") {
        const session = await api<SessionResult>("/api/v1/auth/login", {
          method: "POST",
          ...jsonBody({
            email: form.get("email"),
            password: form.get("password"),
            organization_id: form.get("organization_id") || undefined,
          }),
        });
        if (session.mfa_required && session.mfa_challenge_token) {
          setChallenge(session.mfa_challenge_token);
          return;
        }
        await completeSession(session);
        return;
      }
      if (mode === "register") {
        const accountType = String(form.get("account_type"));
        const session = await api<SessionResult>("/api/v1/auth/register", {
          method: "POST",
          ...jsonBody({
            account_type: accountType,
            display_name: form.get("display_name"),
            email: form.get("email"),
            password: form.get("password"),
            capability_provider: accountType === "supplier" ? form.get("capability_provider") : undefined,
            capability_key: accountType === "supplier" ? form.get("capability_key") : undefined,
            accept_terms_and_privacy: form.get("accept_terms_and_privacy") === "on",
          }),
        });
        await completeSession(session);
        return;
      }
      await api("/api/v1/auth/password-reset/request", {
        method: "POST",
        ...jsonBody({ email: form.get("email") }),
      });
      setMessage("若邮箱已注册，重置邮件已进入发送队列。请检查邮箱。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "请求失败");
    } finally {
      setBusy(false);
    }
  };

  const title = challenge ? "二次验证" : mode === "login" ? "登录企业控制台" : mode === "register" ? "申请企业账号" : "找回账号密码";
  const intro = mode === "login"
    ? "使用企业账号进入对应的运营、平台或供应商工作台。"
    : mode === "register"
      ? "提交组织资料后创建平台方或供应商账号；供应商需同时提交 KEY 能力证明。"
      : "输入企业邮箱，我们会发送一次性密码重置链接。";

  return (
    <AuthLayout>
      <form className={`auth-card auth-card-${mode}`} onSubmit={submit} aria-label={title}>
        <header>
          <p className="section-index">{mode === "login" ? "企业账号登录" : mode === "register" ? "企业账号申请" : "账号安全"}</p>
          <h2>{title}</h2>
          {!challenge && <p className="auth-card-intro">{intro}</p>}
        </header>
        {challenge ? (
          <Field label="验证器或恢复码" name="mfa_code" required autoComplete="one-time-code" placeholder="000000" />
        ) : (
          <>
            {mode === "register" && <RegisterFields providers={providers} />}
            <Field label="邮箱" name="email" type="email" required autoComplete="email" placeholder="name@company.com" />
            {mode !== "reset" && <Field label="密码" name="password" type="password" required autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="至少 12 位" />}
            {mode === "login" && <details className="advanced-login"><summary>同一邮箱属于多个组织？</summary><Field label="组织 ID" name="organization_id" placeholder="仅多组织账号填写 UUID" /></details>}
          </>
        )}
        {error && <Notice tone="danger">{error}</Notice>}
        {message && <Notice tone="success">{message}</Notice>}
        <Button type="submit" wide disabled={busy} aria-label={busy ? challenge ? "正在验证" : mode === "login" ? "正在登录" : mode === "register" ? "正在创建组织" : "正在发送重置邮件" : undefined}>{busy ? <Spinner small /> : challenge ? "验证并继续" : mode === "login" ? "进入控制台" : mode === "register" ? "提交企业账号申请" : "发送重置邮件"}</Button>
        {challenge ? (
          <button type="button" className="text-button" onClick={() => setChallenge("")}>返回密码登录</button>
        ) : (
          <nav className="auth-context-links" aria-label="账号帮助">
            {mode === "login" && <><a href="/forgot-password">忘记密码</a><a href="/register">申请企业账号</a></>}
            {mode === "register" && <a href="/login">已有企业账号，返回登录</a>}
            {mode === "reset" && <a href="/login">返回企业账号登录</a>}
          </nav>
        )}
      </form>
    </AuthLayout>
  );
}

function AuthActionPage({ action, token }: { action: AuthAction; token: string }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(action === "verify-email" && Boolean(token));
  const [error, setError] = useState(token ? "" : "链接缺少一次性令牌，请重新发起申请。");
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (action !== "verify-email" || !token) return;
    let active = true;
    api<{ verified: boolean }>("/api/v1/auth/email/verify", { method: "POST", ...jsonBody({ token }) })
      .then(async (result) => {
        if (!result.verified || !active) return;
        setComplete(true);
        await refresh();
      })
      .catch((caught) => active && setError(caught instanceof Error ? caught.message : "邮箱验证失败"))
      .finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [action, token]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    if (password !== String(form.get("confirm_password") ?? "")) {
      setError("两次输入的密码不一致。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (action === "reset-password") {
        await api("/api/v1/auth/password-reset/complete", { method: "POST", ...jsonBody({ token, new_password: password }) });
      } else {
        await api("/api/v1/auth/invitations/accept", { method: "POST", ...jsonBody({ token, password }) });
      }
      setComplete(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "一次性链接处理失败");
    } finally {
      setBusy(false);
    }
  };

  const title = action === "verify-email" ? "验证邮箱" : action === "reset-password" ? "设置新密码" : "加入组织";
  const success = action === "verify-email" ? "邮箱验证完成，组织状态已刷新。" : action === "reset-password" ? "密码已重置，原有会话已全部撤销。" : "邀请已接受，现在可以使用该账号登录组织。";
  const returnToProduct = () => {
    window.history.replaceState({}, "", action === "verify-email" && getSessionToken() ? "/dashboard" : "/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return <AuthLayout><section className="auth-card auth-action-card" aria-labelledby="auth-action-title">
    <header><p className="section-index">02 / 一次性安全链接</p><h2 id="auth-action-title">{title}</h2></header>
    {busy && <div className="auth-action-state"><Spinner /><span>正在核对一次性令牌</span></div>}
    {!busy && complete && <><Notice tone="success">{success}</Notice>{action === "verify-email"
      ? <Button wide data-audit-route="/verify-email" onClick={returnToProduct}>返回控制台</Button>
      : action === "reset-password"
        ? <Button wide data-audit-route="/reset-password" onClick={returnToProduct}>前往登录</Button>
        : <Button wide data-audit-route="/accept-invitation" onClick={returnToProduct}>前往登录</Button>}</>}
    {!busy && !complete && action === "verify-email" && <><Notice tone="danger">{error || "邮箱验证未完成。"}</Notice><Button wide data-audit-route="/verify-email" tone="quiet" onClick={returnToProduct}>返回登录</Button></>}
    {!busy && !complete && action !== "verify-email" && <form className="stack-form" onSubmit={submit}>
      <Notice>{action === "reset-password" ? "请设置新的强密码。提交后所有旧会话都会失效。" : "新成员请设置强密码；已有账号请输入当前密码确认身份。"}</Notice>
      <Field label={action === "reset-password" ? "新密码" : "密码"} name="password" type="password" required autoComplete="new-password" hint="至少 12 位，并包含大小写、数字与符号" />
      <Field label="确认密码" name="confirm_password" type="password" required autoComplete="new-password" />
      {error && <Notice tone="danger">{error}</Notice>}
      {action === "reset-password"
        ? <Button type="submit" wide data-audit-route="/reset-password" disabled={busy}>完成密码重置</Button>
        : <Button type="submit" wide data-audit-route="/accept-invitation" disabled={busy}>接受组织邀请</Button>}
    </form>}
  </section></AuthLayout>;
}

function AuthLayout({ children }: { children: ReactNode }) {
  return <main className="auth-shell">
    <section className="auth-story" aria-label="平台说明">
      <div className="brand-lockup"><span className="brand-mark">KM</span><span>KEYMARKET / CONTROL</span></div>
      <div className="auth-statement">
        <p className="eyebrow">API CREDENTIAL EXCHANGE</p>
        <h1>企业级 KEY<br />交易与交付。</h1>
        <p>面向供应商、平台采购方和运营团队的企业级 KEY 交易与自动交付平台。供应、采购、推送、用量、结算在同一条可审计链路完成，交易对手匿名，KEY 全程密文。</p>
      </div>
      <div className="auth-proof-strip">
        <span><b>24H</b> 提现解锁</span><span><b>T1-T5</b> 质量分层</span><span><b>RPM</b> 实时回收</span>
      </div>
    </section>
    <section className="auth-panel">{children}</section>
  </main>;
}

function authActionForPath(pathname: string): AuthAction | null {
  if (pathname === "/verify-email") return "verify-email";
  if (pathname === "/reset-password") return "reset-password";
  if (pathname === "/accept-invitation") return "accept-invitation";
  return null;
}

function RegisterFields({ providers }: { providers: Provider[] }) {
  const [type, setType] = useState("platform");
  return (
    <>
      <label className="field"><span>组织类型</span><select name="account_type" value={type} onChange={(event) => setType(event.target.value)}><option value="platform">平台采购方</option><option value="supplier">KEY 供应商</option></select></label>
      <Field label="组织名称" name="display_name" required placeholder="团队或公司名称" />
      {type === "supplier" && (
        <>
          <label className="field"><span>能力证明平台</span><select name="capability_provider" required defaultValue="openai"><option value="">请选择</option>{providers.map((provider) => <option key={provider.code} value={provider.code}>{provider.display_name}</option>)}</select></label>
          <Field label="能力证明 KEY" name="capability_key" type="password" required autoComplete="off" placeholder="只用于加密自动验证" />
        </>
      )}
      <label className="check-line"><input type="checkbox" name="accept_terms_and_privacy" required /> <span>同意服务条款、隐私政策与交易规则</span></label>
    </>
  );
}
